import React, {  useState, useEffect } from 'react';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import styles from './SettingsPage.module.scss';
import settingsService, { CompanyInfo } from '../../services/settingsService';
import bankService, { Bank, CreateBankRequest } from '../../services/bankService';
import { useCompany } from '../../context/CompanyContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authService from '../../services/authService';

const initialCompany: CompanyInfo = {
  name: '',
  address: '',
  email: '',
  phone: '',
  website: '',
  logo: '',
};

const SettingsPage: React.FC = () => {
  const [company, setCompany] = useState<CompanyInfo>(initialCompany);
  const [loading, setLoading] = useState(true);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  
  // Bank state
  // const [banks, setBanks] = useState<Bank[]>([]);
  // const [bankLoading, setBankLoading] = useState(true);
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bankToDelete, setBankToDelete] = useState<Bank | null>(null);
  const [bankForm, setBankForm] = useState<CreateBankRequest>({
    name: '',
    accountNumber: '',
    ifscCode: '',
    branch: '',
    address: ''
  });
  
  // TFA state
  const [tfaEnabled, setTfaEnabled] = useState(false); // Should be fetched from backend
  const [tfaPin, setTfaPin] = useState('');
  const [tfaConfirmPin, setTfaConfirmPin] = useState('');
  const [tfaCurrentPin, setTfaCurrentPin] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);
  const [showTfaEnableModal, setShowTfaEnableModal] = useState(false);
  const [showTfaDisableModal, setShowTfaDisableModal] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const [totpEnabled, setTotpEnabled] = useState(false);
  const [showTotpSetup, setShowTotpSetup] = useState(false);
  const [totpQr, setTotpQr] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  // const [totpError, setTotpError] = useState('');
  console.log("totpEnabled::::::", tfaEnabled)

  // Reset password state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { setName } = useCompany();

  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      try {
        const data = await settingsService.getCompanyInfo();
        const companyData = data && typeof data === 'object' ? data : initialCompany;
        setCompany(companyData);

        setName(companyData.name || initialCompany.name); 
        // setLogoPreview((data && data.logo) ? data.logo : ''); // Removed
      } catch (e) {
        console.error('Error fetching company info:', e);
        toast.error('Failed to load company information');
        setName(initialCompany.name); 
        setCompany(initialCompany);
        // setLogoPreview(''); // Removed
      }
      setLoading(false);
    };
    fetchCompany();
  }, [setName]);

  useEffect(() => {
    const fetchTfaStatus = async () => {
      try {
        const { enabled } = await settingsService.getTfaStatus();
        console.log("resData::::::::::", enabled)
        setTfaEnabled(enabled);
      } catch (e) {
        setTfaEnabled(false);
      }
    };
    fetchTfaStatus();
  }, []);

  useEffect(() => {
    const fetchTotpStatus = async () => {
      try {
        const { enabled } = await authService.get2FAStatus() as { enabled: boolean };
        setTotpEnabled(enabled);
      } catch {
        setTotpEnabled(false);
      }
    };
    fetchTotpStatus();
  }, []);

  const fetchBanks = async () => {
    // setBankLoading(true);
    try {
      const data = await bankService.getBanks();
      // Ensure data is an array and filter out any invalid entries
      const validBanks = Array.isArray(data) ? data.filter(bank => bank && typeof bank === 'object' && bank.id) : [];
      console.log('Fetched banks:', validBanks);
      // setBanks(validBanks);
    } catch (e) {
      console.error('Error fetching banks:', e);
      toast.error('Failed to load bank information');
      // setBanks([]);
    }
    // setBankLoading(false);
  };

  // Fetch banks
  useEffect(() => {
    fetchBanks();
  }, []);

  const handleCompanyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCompany({ ...company, [e.target.name]: e.target.value });
  };

  // const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => { // Removed
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     // 1. Upload to backend and get the path
  //     const logoPath = await settingsService.uploadLogo(file);
  //     // 2. Update company state and preview
  //     setCompany(prev => ({ ...prev, logo: logoPath }));
  //     setLogoPreview(`/${logoPath}`);
  //   }
  // };

  // const handleBackup = () => {
  //   // Simulate backup download
  //   toast.success('Backup created (mock).');
  // };

  const handleRestore = () => {
    setShowRestoreModal(false);
    toast.success('Data restored (mock).');
  };

  const handleUploadBackup = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setBackupFile(file);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updated = await settingsService.updateCompanyInfo(company);
      setCompany(updated);
      setName(updated.name); 
      toast.success('Company info saved!');
    } catch (e) {
      console.log("response::::::", e)
      toast.error('Failed to save company info');
    } finally {
      setLoading(false);
    }
  };

  // Bank functions
  const handleBankChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBankForm({ ...bankForm, [e.target.name]: e.target.value });
  };

  // const handleAddBank = () => {
  //   setEditingBank(null);
  //   setIsViewMode(false);
  //   setBankForm({
  //     name: '',
  //     accountNumber: '',
  //     ifscCode: '',
  //     branch: '',
  //     address: ''
  //   });
  //   setShowBankModal(true);
  // };

  // const handleViewBank = (bank: Bank) => {
  //   if (!bank || !bank.id) {
  //     toast.error('Invalid bank data');
  //     return;
  //   }
  //   setEditingBank(bank);
  //   setIsViewMode(true);
  //   setBankForm({
  //     name: bank.name || '',
  //     accountNumber: bank.account_number || '',
  //     ifscCode: bank.ifsc_code || '',
  //     branch: bank.branch || '',
  //     address: bank.address || ''
  //   });
  //   setShowBankModal(true);
  // };

  // const handleEditBank = (bank: Bank) => {
  //   if (!bank || !bank.id) {
  //     toast.error('Invalid bank data');
  //     return;
  //   }
  //   setEditingBank(bank);
  //   setIsViewMode(false);
  //   setBankForm({
  //     name: bank.name || '',
  //     accountNumber: bank.account_number || '',
  //     ifscCode: bank.ifsc_code || '',
  //     branch: bank.branch || '',
  //     address: bank.address || ''
  //   });
  //   setShowBankModal(true);
  // };

  // const handleDeleteBank = (bank: Bank) => {
  //   if (!bank || !bank.id) {
  //     toast.error('Invalid bank data');
  //     return;
  //   }
  //   setBankToDelete(bank);
  //   setShowDeleteModal(true);
  // };

  const confirmDeleteBank = async () => {
    if (!bankToDelete || !bankToDelete.id) {
      toast.error('Invalid bank data');
      return;
    }
    
    try {
      await bankService.deleteBank(bankToDelete.id);
      await fetchBanks(); // Refresh the list
      toast.success('Bank deleted successfully');
      setShowDeleteModal(false);
      setBankToDelete(null);
    } catch (e) {
      console.error('Error deleting bank:', e);
      toast.error('Failed to delete bank');
    }
  };

  const handleSaveBank = async () => {
    try {
      if (editingBank) {
        console.log('Updating bank with data:', { ...bankForm, id: editingBank.id });
        const updated = await bankService.updateBank({ ...bankForm, id: editingBank.id });
        console.log('Updated bank response:', updated);
        
        toast.success('Bank updated successfully');
      } else {
        console.log('Creating new bank with data:', bankForm);
        const newBank = await bankService.createBank(bankForm);
        console.log('New bank response:', newBank);
        
        toast.success('Bank added successfully');
      }
      
      // Refresh the banks list to ensure UI is up to date
      await fetchBanks();
      
      setShowBankModal(false);
      setEditingBank(null);
      setIsViewMode(false);
      setBankForm({
        name: '',
        accountNumber: '',
        ifscCode: '',
        branch: '',
        address: ''
      });
    } catch (e) {
      console.error('Error saving bank:', e);
      toast.error('Failed to save bank');
    }
  };

  const handleTfaToggle = (checked: boolean) => {
    // if (checked) {
    //   setShowTfaEnableModal(true);
    // } else {
    //   setShowTfaDisableModal(true);
    // }
    if (checked) {
      handleEnableTotp();
    } else {
      handleDisableTotp();
    }
  };

  const handleEnableTfa = async () => {
    if (!tfaPin || !tfaConfirmPin) {
      toast.error('Please enter and confirm your PIN');
      return;
    }
    if (tfaPin !== tfaConfirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setTfaLoading(true);
    try {
      await settingsService.enableTfa(tfaPin, tfaConfirmPin);
      setTfaEnabled(true);
      setTfaPin('');
      setTfaConfirmPin('');
      setShowTfaEnableModal(false);
      toast.success('TFA enabled successfully');
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        (e?.response?.status === 401
          ? 'Invalid PIN. Please try again.'
          : 'Failed to enable TFA');
      toast.error(msg);
    } finally {
      setTfaLoading(false);
    }
  };

  const handleDisableTfa = async () => {
   
    if (!tfaCurrentPin) {
      toast.error('Please enter your current PIN');
      return;
    }
    setTfaLoading(true);
    try {
      await settingsService.disableTfa(tfaCurrentPin);
      setTfaEnabled(false);
      setTfaCurrentPin('');
      setShowTfaDisableModal(false);
      toast.success('TFA disabled successfully');
    } catch (e: any) {
      // Show toast for wrong PIN or other errors
      const msg =
        e?.response?.data?.error ||
        (e?.response?.status === 401
          ? 'Invalid PIN. Please try again.'
          : 'Failed to disable TFA');
      toast.error(msg);
    } finally {
      setTfaLoading(false);
    }
  };

  const handleEnableTotp = async () => {
    setTotpLoading(true);
    // setTotpError('');
    try {
      const { qr, secret } = await authService.setup2FA() as { qr: string; secret: string };
      setTotpQr(qr);
      setTotpSecret(secret);
      setShowTotpSetup(true);
    } catch (e) {
      console.log("response::::::", e)
      toast.error('Failed to start 2FA setup.');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyTotp = async () => {
    setTotpLoading(true);
    // setTotpError('');
    try {
      await authService.verify2FA(totpCode);
      setTotpEnabled(true);
      setShowTotpSetup(false);
      setTotpCode('');
      setTotpQr('');
      setTotpSecret('');
      toast.success('Google Authenticator enabled!');
    } catch (e) {
      console.error('2FA verification failed:', e);
      // Check if it might be a time sync issue
      const now = new Date();
      const timeOffset = Math.abs(now.getTime() - Date.now());
      
      if (timeOffset > 30000) { // 30 seconds
        toast.error('Invalid code. Please check your device time is synchronized with the internet.');
      } else {
        toast.error('Invalid code. Please try again or check your device time is correct.');
      }
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    setTotpLoading(true);
    // setTotpError('');
    try {
      await authService.disable2FA();
      setTotpEnabled(false);
      toast.success('Google Authenticator disabled.');
    } catch (e) {
      toast.error('Failed to disable 2FA.');
    } finally {
      setTotpLoading(false);
    }
  };

  // Reset password functions
  const handleResetPasswordChange = (field: keyof typeof resetPasswordForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setResetPasswordForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm.currentPassword || !resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (resetPasswordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await authService.resetPassword(resetPasswordForm.currentPassword, resetPasswordForm.newPassword);
      toast.success('Password updated successfully');
      setShowResetPasswordModal(false);
      setResetPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to update password';
      toast.error(message);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2>Settings</h2>
      <div className={styles.companyInfo}>
        <div className={styles.card}>
          <h3>Company Information</h3>
          <div className={styles.companyFormColumn}>
            {/* Logo section hidden
            <div className={styles.logoSectionCentered}>
              <div
                className={styles.logoPreview}
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                {company && company.logo ? (
                  <>
                    <img src={logoPreview} alt="Logo Preview" />
                    <span className={styles.editIcon}>✎</span>
                  </>
                ) : (
                  <span className={styles.logoPlaceholder}>No Logo</span>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
            </div>
            */}
            <div className={styles.infoFieldsColumn}>
              <label>Company Name</label>
              <input name="name" value={company?.name || ''} onChange={handleCompanyChange} placeholder="Enter company name" />
              <label>Address</label>
              <textarea
                name="address"
                value={company?.address || ''}
                onChange={handleCompanyChange}
                placeholder="Enter address"
                rows={3}
              />
              <label>Contact Email</label>
              <input name="email" value={company?.email || ''} onChange={handleCompanyChange} placeholder="Enter contact email" />
              <label>Phone Number</label>
              <input name="phone" value={company?.phone || ''} onChange={handleCompanyChange} placeholder="Enter phone number" />
              <label>Website URL</label>
              <input name="website" value={company?.website || ''} onChange={handleCompanyChange} placeholder="Enter website URL" />
            </div>
            <Button
              variant="primary"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
      <hr className={styles.sectionDivider} />
      
      {/* Reset Password Section */}
      <div className={styles.card}>
        <h3>Reset Password</h3>
        <div className={styles.resetPasswordSection}>
          <p>Change your account password to keep your account secure.</p>
          <Button
            variant="primary"
            onClick={() => setShowResetPasswordModal(true)}
          >
            Reset Password
          </Button>
        </div>
      </div>
      <hr className={styles.sectionDivider} />
      
      {/* Bank List Section */}
      {/* <div className={styles.bankSection}>
        <div className={styles.card}>
          <div className={styles.bankHeader}>
            <h2>
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '12px', verticalAlign: 'middle' }}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              Bank Accounts
            </h2>
            <button className={styles.addBankButton} onClick={handleAddBank}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Bank
            </button>
          </div> */}
          
          {/* {bankLoading ? (
            <div className={styles.noBanks}>
              <p>Loading banks...</p>
            </div>
          ) : banks.length === 0 ? (
            <div className={styles.noBanks}>
              <p>No bank accounts found</p>
              <p>Add your first bank account to get started</p>
            </div>
          ) : (
            <div className={styles.bankList}>
              <table className={styles.bankTable}>
                <thead>
                  <tr>
                    <th>Bank Name</th>
                    <th>Account Number</th>
                    <th>IFSC Code</th>
                    <th>Branch</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {banks.filter(bank => bank && bank.id).map((bank) => (
                    <tr key={bank.id}>
                      <td>{bank.name || 'N/A'}</td>
                      <td>{bank.account_number || 'N/A'}</td>
                      <td>{bank.ifsc_code || 'N/A'}</td>
                      <td>{bank.branch || 'N/A'}</td>
                      <td>
                        <div className={styles.bankActions}>
                          <button
                            className={styles.viewBankButton}
                            onClick={() => handleViewBank(bank)}
                            title="View Bank Details"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                          </button>
                          <button
                            className={styles.editBankButton}
                            onClick={() => handleEditBank(bank)}
                            title="Edit Bank"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          </button>
                          <button
                            className={styles.deleteBankButton}
                            onClick={() => handleDeleteBank(bank)}
                            title="Delete Bank"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div> */}
      
      {/* <div className={styles.card}>
        <h2>Data Management</h2>
        <div className={styles.dataActions}>
          <Button variant="secondary" onClick={handleBackup}>
            <span style={{ marginRight: 8, display: 'inline-flex', verticalAlign: 'middle' }}>
              <svg width="25" height="25" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            </span>
            Backup Data
          </Button>
          <Button variant="success" onClick={() => setShowUploadModal(true)}>
            <span style={{ marginRight: 8, display: 'inline-flex', verticalAlign: 'middle' }}>
              <svg width="25" height="25" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 17V3"/>
                <polyline points="5 10 12 3 19 10"/>
                <rect x="3" y="17" width="18" height="4" rx="2"/>
              </svg>
            </span>
            Restore Data
          </Button>
        </div>
      </div> */}
      <Modal isOpen={showRestoreModal} onClose={() => setShowRestoreModal(false)} title="Confirm Restore">
        <p>Are you sure you want to restore data from this backup? This will overwrite current data.</p>
        <div className={styles.modalActions}>
          <Button variant="danger" onClick={handleRestore}>Yes, Restore</Button>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>Cancel</Button>
        </div>
      </Modal>
      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Backup File">
        <input type="file" accept=".json,.zip" onChange={handleUploadBackup} />
        <div className={styles.modalActions}>
          <Button
            variant="primary"
            onClick={() => {
              setShowUploadModal(false);
              setShowRestoreModal(true);
            }}
            disabled={!backupFile}
          >
            Next
          </Button>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>Cancel</Button>
        </div>
      </Modal>
      
      {/* Bank Modal */}
      <Modal 
        isOpen={showBankModal} 
        onClose={() => {
          setShowBankModal(false);
          setEditingBank(null);
          setIsViewMode(false);
          setBankForm({
            name: '',
            accountNumber: '',
            ifscCode: '',
            branch: '',
            address: ''
          });
        }} 
        title={
          isViewMode 
            ? 'View Bank Account' 
            : editingBank 
              ? 'Edit Bank Account' 
              : 'Add Bank Account'
        }
      >
        <div className={styles.bankForm}>
          <div className={styles.bankFormGroup}>
            <label>Bank Name</label>
            <input
              type="text"
              name="name"
              value={bankForm.name}
              onChange={handleBankChange}
              placeholder="Enter bank name"
              readOnly={isViewMode}
            />
          </div>
          <div className={styles.bankFormGroup}>
            <label>Account Number</label>
            <input
              type="text"
              name="accountNumber"
              value={bankForm.accountNumber}
              onChange={handleBankChange}
              placeholder="Enter account number"
              readOnly={isViewMode}
            />
          </div>
          <div className={styles.bankFormGroup}>
            <label>IFSC Code</label>
            <input
              type="text"
              name="ifscCode"
              value={bankForm.ifscCode}
              onChange={handleBankChange}
              placeholder="Enter IFSC code"
              readOnly={isViewMode}
            />
          </div>
          <div className={styles.bankFormGroup}>
            <label>Branch</label>
            <input
              type="text"
              name="branch"
              value={bankForm.branch}
              onChange={handleBankChange}
              placeholder="Enter branch name"
              readOnly={isViewMode}
            />
          </div>
          <div className={styles.bankFormGroup}>
            <label>Address</label>
            <textarea
              name="address"
              value={bankForm.address}
              onChange={handleBankChange}
              placeholder="Enter bank address"
              readOnly={isViewMode}
            />
          </div>
        </div>
        <div className={styles.modalActions}>
          {!isViewMode && (
            <Button variant="primary" onClick={handleSaveBank}>
              {editingBank ? 'Update Bank' : 'Add Bank'}
            </Button>
          )}
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowBankModal(false);
              setEditingBank(null);
              setIsViewMode(false);
              setBankForm({
                name: '',
                accountNumber: '',
                ifscCode: '',
                branch: '',
                address: ''
              });
            }}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
        </div>
      </Modal>
      
      {/* Delete Bank Confirmation Modal */}
      <Modal 
        isOpen={showDeleteModal} 
        onClose={() => {
          setShowDeleteModal(false);
          setBankToDelete(null);
        }} 
        title="Delete Bank Account"
      >
        <div className={styles.deleteConfirmation}>
          <div className={styles.deleteWarning}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <div>
              <h4>Are you sure you want to delete this bank account?</h4>
              <p>This action cannot be undone. The bank account will be permanently removed.</p>
              {bankToDelete && (
                <div className={styles.bankToDeleteInfo}>
                  <p><strong>Bank:</strong> {bankToDelete.name}</p>
                  <p><strong>Account:</strong> {bankToDelete.account_number}</p>
                  <p><strong>Branch:</strong> {bankToDelete.branch}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.modalActions}>
          <Button variant="danger" onClick={confirmDeleteBank}>
            Delete Bank Account
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setBankToDelete(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>
      
      {/* TFA Section */}
      {/* <div className={styles.card}>
        <h2>Two-Factor Authentication (TFA)</h2>
        <div className={styles.tfaSection}>
          <div
            className={styles.tfaToggle}
            role="button"
            tabIndex={0}
            aria-pressed={tfaEnabled}
            onClick={() => handleTfaToggle(!tfaEnabled)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleTfaToggle(!tfaEnabled);
            }}
            style={{ outline: 'none' }}
          >
            <div className={tfaEnabled ? styles.toggleOn : styles.toggleOff}>
              <div className={styles.toggleKnob} />
            </div>
            <span className={styles.toggleLabel}>
              {tfaEnabled ? 'TFA Enabled' : 'TFA Enable'}
            </span>
          </div>
          <div className={styles.tfaStatusMsg}>
            {tfaEnabled
              ? 'Your account is protected with a PIN. Toggle off to disable.'
              : 'Add an extra layer of security to your account with a PIN.'}
          </div>
          
        </div>
      </div> */}

      {/* TFA Enable Modal */}
      <Modal
        isOpen={showTfaEnableModal}
        onClose={() => {
          setShowTfaEnableModal(false);
          setTfaPin('');
          setTfaConfirmPin('');
          setShowPin(false);
          setShowConfirmPin(false);
        }}
        title="Enable TFA"
      >
        <div className={styles.tfaEnableForm}>
          <label>Enter New PIN</label>
          <div className={styles.pinInputRow}>
            <input
              type={showPin ? 'text' : 'password'}
              value={tfaPin}
              onChange={e => setTfaPin(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter new PIN"
              maxLength={6}
              minLength={4}
              disabled={tfaLoading}
              autoComplete="new-password"
            />
            <button
              type="button"
              className={styles.pinToggleBtn}
              onClick={() => setShowPin(v => !v)}
              tabIndex={-1}
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
              disabled={tfaLoading}
            >
              {showPin ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              ) : (
                <svg
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
  <circle cx="12" cy="12" r="3.5" />
  <line x1="3" y1="3" x2="21" y2="21" />
</svg>
              )}
            </button>
          </div>
          <label>Confirm New PIN</label>
          <div className={styles.pinInputRow}>
            <input
              type={showConfirmPin ? 'text' : 'password'}
              value={tfaConfirmPin}
              onChange={e => setTfaConfirmPin(e.target.value.replace(/\D/g, ''))}
              placeholder="Confirm new PIN"
              maxLength={6}
              minLength={4}
              disabled={tfaLoading}
              autoComplete="new-password"
            />
            <button
              type="button"
              className={styles.pinToggleBtn}
              onClick={() => setShowConfirmPin(v => !v)}
              tabIndex={-1}
              aria-label={showConfirmPin ? 'Hide PIN' : 'Show PIN'}
              disabled={tfaLoading}
            >
              {showConfirmPin ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              ) : (
                <svg
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
  <circle cx="12" cy="12" r="3.5" />
  <line x1="3" y1="3" x2="21" y2="21" />
</svg>
              )}
            </button>
            {/* Success checkmark if valid and match */}
            {tfaPin.length >= 4 && tfaPin === tfaConfirmPin && tfaPin !== '' && (
              <span className={styles.pinMatchCheck} title="PINs match">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="green" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
            )}
          </div>
          <div className={styles.pinHelperText}>
            PIN must be 4-6 digits. Only numbers allowed.
          </div>
          {/* Real-time validation */}
          {tfaPin && tfaPin.length < 4 && (
            <div className={styles.pinError}>PIN must be at least 4 digits.</div>
          )}
          {tfaPin && tfaConfirmPin && tfaPin !== tfaConfirmPin && (
            <div className={styles.pinError}>PINs do not match.</div>
          )}
        </div>
        <div className={styles.modalActions}>
          <Button
            variant="primary"
            onClick={handleEnableTfa}
            disabled={tfaLoading || tfaPin.length < 4 || tfaPin !== tfaConfirmPin}
          >
            {tfaLoading ? 'Enabling...' : 'Enable TFA'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowTfaEnableModal(false);
              setTfaPin('');
              setTfaConfirmPin('');
              setShowPin(false);
              setShowConfirmPin(false);
            }}
            disabled={tfaLoading}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      {/* TFA Disable Modal */}
      <Modal
        isOpen={showTfaDisableModal}
        onClose={() => {
          setShowTfaDisableModal(false);
          setTfaCurrentPin('');
        }}
        title="Disable TFA - Confirm PIN"
      >
        <div className={styles.tfaDisableForm}>
          <label>Enter Current PIN to Disable TFA</label>
          <input
            type="password"
            value={tfaCurrentPin}
            onChange={e => setTfaCurrentPin(e.target.value)}
            placeholder="Enter current PIN"
            maxLength={6}
            disabled={tfaLoading}
          />
        </div>
        <div className={styles.modalActions}>
          <Button
            variant="danger"
            onClick={handleDisableTfa}
            disabled={tfaLoading || !tfaCurrentPin}
          >
            {tfaLoading ? 'Disabling...' : 'Disable TFA'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowTfaDisableModal(false);
              setTfaCurrentPin('');
            }}
            disabled={tfaLoading}
          >
            Cancel
          </Button>
        </div>
      </Modal>
      
      {/* Google Authenticator (TOTP) Section */}
      <div className={styles.card}>
        <h2>Two-Factor Authentication (TFA)</h2>
        <div className={styles.tfaSection}>
          <div
            className={styles.tfaToggle}
            role="button"
            tabIndex={0}
            aria-pressed={totpEnabled}
            onClick={() => handleTfaToggle(!totpEnabled)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleTfaToggle(!totpEnabled);
            }}
            style={{ outline: 'none' }}
          >
            <div className={totpEnabled ? styles.toggleOn : styles.toggleOff}>
              <div className={styles.toggleKnob} />
            </div>
            <span className={styles.toggleLabel}>
              {totpEnabled ? 'TFA Enabled' : 'TFA Enable'}
            </span>
          </div>
          <div className={styles.tfaStatusMsg}>
            {totpEnabled
              ? 'Google Authenticator is enabled for your account.'
              : 'Protect your account with Google Authenticator.'}
          </div>
          
        </div>
        
      </div>

      <Modal isOpen={showTotpSetup} onClose={() => setShowTotpSetup(false)} title="Set up Google Authenticator" size='xl'>
          <div className={styles.tfaEnableForm}>
            <p>1. Scan this QR code with your Google Authenticator app:</p>
            {totpQr && <img src={totpQr} alt="TOTP QR Code" style={{ width: 200, height: 200 }} />}
            <p>Or enter this secret manually: <b>{totpSecret}</b></p>
            <p>2. Enter the 6-digit code from your app:</p>
            <input
              type="text"
              value={totpCode}
              onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              maxLength={6}
              disabled={totpLoading}
            />
          </div>
          <div className={styles.modalActions}>
            <Button variant="primary" onClick={handleVerifyTotp} disabled={totpLoading || totpCode.length !== 6}>
              {totpLoading ? 'Verifying...' : 'Verify & Enable'}
            </Button>
            <Button variant="secondary" onClick={() => setShowTotpSetup(false)} disabled={totpLoading}>
              Cancel
            </Button>
          </div>
        </Modal>
      
      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setResetPasswordForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }}
        title="Reset Password"
      >
        <div className={styles.resetPasswordForm}>
          <div className={styles.formGroup}>
            <label>Current Password</label>
            <div className={styles.passwordInputRow}>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={resetPasswordForm.currentPassword}
                onChange={handleResetPasswordChange('currentPassword')}
                placeholder="Enter current password"
                disabled={resetPasswordLoading}
              />
              <button
                type="button"
                className={styles.passwordToggleBtn}
                onClick={() => setShowCurrentPassword(v => !v)}
                tabIndex={-1}
                aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                disabled={resetPasswordLoading}
              >
                {showCurrentPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3.5"/>
                    <line x1="3" y1="3" x2="21" y2="21"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>New Password</label>
            <div className={styles.passwordInputRow}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={resetPasswordForm.newPassword}
                onChange={handleResetPasswordChange('newPassword')}
                placeholder="Enter new password"
                disabled={resetPasswordLoading}
              />
              <button
                type="button"
                className={styles.passwordToggleBtn}
                onClick={() => setShowNewPassword(v => !v)}
                tabIndex={-1}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                disabled={resetPasswordLoading}
              >
                {showNewPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3.5"/>
                    <line x1="3" y1="3" x2="21" y2="21"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Confirm New Password</label>
            <div className={styles.passwordInputRow}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={resetPasswordForm.confirmPassword}
                onChange={handleResetPasswordChange('confirmPassword')}
                placeholder="Confirm new password"
                disabled={resetPasswordLoading}
              />
              <button
                type="button"
                className={styles.passwordToggleBtn}
                onClick={() => setShowConfirmPassword(v => !v)}
                tabIndex={-1}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                disabled={resetPasswordLoading}
              >
                {showConfirmPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3.5"/>
                    <line x1="3" y1="3" x2="21" y2="21"/>
                  </svg>
                )}
              </button>
            </div>
            {/* Password match indicator */}
            {resetPasswordForm.newPassword && resetPasswordForm.confirmPassword && (
              <div className={resetPasswordForm.newPassword === resetPasswordForm.confirmPassword ? styles.passwordMatch : styles.passwordMismatch}>
                {resetPasswordForm.newPassword === resetPasswordForm.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}
          </div>

          <div className={styles.passwordRequirements}>
            <p>Password requirements:</p>
            <ul>
              <li>At least 6 characters long</li>
              <li>Should be different from your current password</li>
            </ul>
          </div>
        </div>
        <div className={styles.modalActions}>
          <Button
            variant="primary"
            onClick={handleResetPassword}
            disabled={resetPasswordLoading || !resetPasswordForm.currentPassword || !resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword || resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword || resetPasswordForm.newPassword.length < 6}
          >
            {resetPasswordLoading ? 'Updating...' : 'Update Password'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowResetPasswordModal(false);
              setResetPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
              });
            }}
            disabled={resetPasswordLoading}
          >
            Cancel
          </Button>
        </div>
      </Modal>
      
      <ToastContainer />
    </div>
  );
};

export default SettingsPage; 